import { Injectable } from '@angular/core';

const STORAGE_KEY = 'csd.fcra.satisfied';

/**
 * Tracks whether the FCRA / T&C step has been satisfied for the current login
 * session (FDD §3.1). Backed by `sessionStorage` so its lifetime matches the OIDC
 * token (angular-oauth2-oidc's default store): it survives a same-tab refresh —
 * so an ordinary reload after acceptance does NOT re-prompt — but is gone once the
 * tab closes and the session ends. Each new login resets it (see CallbackComponent)
 * so FCRA is presented once per login. While it is unset (a fresh login, or a
 * refresh taken while the modal is still up), the fcraGuard re-shows FCRA.
 *
 * Storage access is defensive: if `sessionStorage` is unavailable (private mode,
 * blocked cookies) the gate reports "not satisfied" so FCRA is shown rather than
 * silently skipped — fail safe for a compliance gate.
 */
@Injectable({ providedIn: 'root' })
export class FcraGateService {
  isSatisfied(): boolean {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /** Record that FCRA has been accepted/acknowledged for this login session. */
  markSatisfied(): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // No persistence available — the guard will simply re-prompt next navigation.
    }
  }

  /** Clear the flag so FCRA is presented again (called on each new login). */
  reset(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing persisted to clear.
    }
  }
}
